<script lang="ts">
  import type { IMessage } from "src/interfaces/Chat";
  import { parseDateWithTime } from "../../lib/parseDateWithTime";
  import { urlify } from "../../lib/urlify";
  import { user } from "stores";
  import { translate } from "lib";

  export let message: IMessage;
  export let isMine: boolean;
  export let touchstart: (
    messageId: number,
    messagePayload: string,
    timestamp: any,
    isMine: boolean,
    deletedAt: string | null
  ) => void;
  export let touchend: () => void;
  export let isSelected: boolean = false;
  export let onSelect: (messageId: number) => void = () => {};
  export let sharedSenderLabel: string = "";
  export let isSharedTrainerChatEnabled: boolean = false;
  export let onHistoryOpen: (message: IMessage) => void = () => {};

  let showTimestamp = false;
  const editHistoryThresholdMs = 500;

  const toggleTimestamp = (): void => {
    showTimestamp = !showTimestamp;
  };

  const hasEditHistory = (): boolean => {
    const createdAt = new Date(message.createdAt).getTime();
    const updatedAt = new Date(message.updatedAt).getTime();

    if (Number.isNaN(createdAt) || Number.isNaN(updatedAt)) {
      return false;
    }

    return updatedAt - createdAt > editHistoryThresholdMs;
  };

  const canOpenHistory = (): boolean => {
    return Boolean(isSharedTrainerChatEnabled && (message.deletedAt || hasEditHistory()));
  };

  function handleClick(event: MouseEvent) {
    const isAiTrainer = localStorage.getItem("ai_trainer") === "true";

    if (isAiTrainer) {
      event.preventDefault();
      event.stopPropagation();

      onSelect(message.id);
      return;
    }

    if (canOpenHistory()) {
      event.preventDefault();
      event.stopPropagation();
      onHistoryOpen(message);
      return;
    }

    toggleTimestamp();
  }

  function handleKeypress(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
      const isAiTrainer = localStorage.getItem("ai_trainer") === "true";

      if (isAiTrainer && event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        onSelect(message.id);
        return;
      }

      event.preventDefault();

      if (canOpenHistory()) {
        onHistoryOpen(message);
        return;
      }

      toggleTimestamp();
    }
  }

  $: align = isMine ? "flex-end" : "flex-start";
  $: backgroundColor = message.deletedAt
    ? "bg-slate-100 dark:bg-zinc-800"
    : isSelected
      ? "bg-blue-200 dark:bg-blue-800"
      : isMine
        ? showTimestamp
          ? "bg-primary-700 dark:bg-primary-700"
          : "bg-primary-500 dark:bg-primary-500"
        : showTimestamp
          ? "bg-slate-100 dark:bg-zinc-900"
          : "";
  $: color = message.deletedAt
    ? "text-red-900 dark:text-red-200"
    : isMine
      ? "text-slate-100 dark:text-slate-100"
      : "text-slate-900 dark:text-slate-100";
  $: border = message.deletedAt
    ? "dark:border dark:border-zinc-600"
    : isSelected
      ? "border-2 border-blue-500 dark:border-blue-400"
      : isMine
        ? ""
        : "border border-slate-300 dark:border-zinc-600";
  $: metadataText = sharedSenderLabel
    ? `${parseDateWithTime(message.createdAt)} - ${sharedSenderLabel}`
    : parseDateWithTime(message.createdAt);
</script>

<div
  unselectable="on"
  translate="no"
  id={`${message?.id}`}
  on:mousedown={() => touchstart(
    message.id,
    message.payload,
    message.createdAt,
    isMine,
    message.deletedAt
  )}
  on:mouseup={() => touchend()}
  on:touchend={() => touchend()}
  on:touchstart={() => touchstart(
    message.id,
    message.payload,
    message.createdAt,
    isMine,
    message.deletedAt
  )}
  on:click={handleClick}
  on:keypress={handleKeypress}
  role="button"
  tabindex="0"
  class={`message max-w-80 font-sans whitespace-pre-wrap mgl-8 mgr-8 mt-1 ${color} ${backgroundColor} ${border} ${isSelected ? 'ring-2 ring-blue-400' : ''} transition-all duration-200`}
  style={`
  font-size: ${$user.id === 13 ? 13 : 14}px;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -o-user-select: none;
  align-self: ${align};`}
>
  {#if message?.deletedAt}
    <p class="italic">{translate("DELETED_MESSAGE")}</p>
  {:else}
    {@html urlify(message?.payload)}
  {/if}
</div>
{#if showTimestamp}
  <div
    class="ml-3 mr-3 mt-1 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
    style={`align-self: ${align}`}
  >
    <span>{metadataText}</span>
  </div>
{/if}

<style>
  .message {
    align-self: flex-end;
    padding: 6px 10px 6px 10px;
    border-radius: 16px;
    width: fit-content;
  }
</style>
